/**
 * Deterministic serialisation for request and cache keys.
 *
 * Keys are derived from structured context objects — never from concatenated
 * strings — so user-entered values (Area names, DMA idents, search terms) cannot
 * collide with a delimiter or a sentinel. Discriminated selectors are kept as
 * objects end-to-end, so an assigned selector whose ident literally equals
 * '__unassigned__' still serialises differently from the unassigned selector.
 *
 * Object keys are sorted recursively, so property insertion order never changes
 * the result. Arrays keep their order (it is meaningful).
 */
function normalise(value: unknown): unknown {
    if (value === null || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.map(normalise);

    const source = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
        // Treat an explicit `undefined` the same as an absent key, so optional
        // fields cannot produce two keys for one logical context.
        if (source[key] !== undefined) sorted[key] = normalise(source[key]);
    }
    return sorted;
}

export function stableKey(value: unknown): string {
    return JSON.stringify(normalise(value));
}
